import { useState, useCallback, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { trackSignUp, trackGenerateLead, trackSelectItem } from '../lib/gtag';
import { trackTrialSignup } from '../lib/lytag';
import CtaButton from './CtaButton';
import CheckoutModal from './CheckoutModal';
import { useVariant } from '../context/VariantContext';
import {
  type DeviceUserRelation,
  RELATION_OPTIONS,
  FORM_LABELS,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
  ZIPCLOUD_API_URL,
  sanitizePostalCode,
  sanitizePhoneNumber,
  sanitizeDisplayName,
  validateRegistrationForm,
  buildUserDocument,
  type RegistrationFormData,
} from '@shared/registration';

// 紹介コード検証 API のベースURL
const FUNCTIONS_BASE_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || '';

export default function Join() {
  const { join, pricing } = useVariant();
  const plans = pricing.plans;
  const showPlanSelector = plans.length >= 2;
  // プラン選択
  const [selectedPlan, setSelectedPlan] = useState(plans[0].id);

  // アカウント情報
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [hasReferral, setHasReferral] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [referralAgentName, setReferralAgentName] = useState('');

  // 配送先（常時表示）
  const [postalCode, setPostalCode] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [city, setCity] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userRelation, setUserRelation] = useState<DeviceUserRelation | ''>('');
  const [userRelationOther, setUserRelationOther] = useState('');
  const [addressSearching, setAddressSearching] = useState(false);

  // オプション
  const [wantsWifiPack, setWantsWifiPack] = useState(false);
  const [wantsSetupSupport, setWantsSetupSupport] = useState(false);

  // その他
  const [question, setQuestion] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToCrossBorder, setAgreedToCrossBorder] = useState(false);

  // UI状態
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Stripe 決済モーダル
  const [checkoutClientSecret, setCheckoutClientSecret] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);

  // フォームが画面内に入ったらフローティングCTAを非表示
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle('hide-floating-cta', entry.isIntersecting);
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => { observer.disconnect(); document.body.classList.remove('hide-floating-cta'); };
  }, []);

  // 郵便番号から住所検索
  const searchAddress = useCallback(async (code: string) => {
    const clean = code.replace(/[^0-9]/g, '');
    if (clean.length !== 7) return;
    setAddressSearching(true);
    try {
      const res = await fetch(`${ZIPCLOUD_API_URL}?zipcode=${clean}`);
      const data = await res.json();
      if (data.results?.length > 0) {
        const r = data.results[0];
        setPrefecture(r.address1);
        setCity(r.address2);
        setFullAddress(r.address1 + r.address2 + (r.address3 || ''));
      }
    } catch (err) {
      console.error('Address search error:', err);
    } finally {
      setAddressSearching(false);
    }
  }, []);

  const handlePostalCodeChange = (val: string) => {
    const clean = sanitizePostalCode(val);
    setPostalCode(clean);
    if (clean.length === 7) searchAddress(clean);
  };

  const handlePhoneChange = (val: string) => {
    setPhoneNumber(sanitizePhoneNumber(val));
  };

  // 紹介コードチェック切り替え
  const handleReferralToggle = (checked: boolean) => {
    setHasReferral(checked);
    if (!checked) {
      setReferralCode('');
      setReferralValid(null);
      setReferralAgentName('');
    }
  };

  // 紹介コード一覧をAPIから取得
  const [referralOptions, setReferralOptions] = useState<{ code: string; agentName: string }[]>([]);
  useEffect(() => {
    fetch(`${FUNCTIONS_BASE_URL}/referral/list`)
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setReferralOptions(data); })
      .catch(() => {});
  }, []);

  const handleReferralSelect = (code: string) => {
    setReferralCode(code);
    if (!code) {
      setReferralValid(null);
      setReferralAgentName('');
      return;
    }
    const option = referralOptions.find(o => o.code === code);
    if (option) {
      setReferralValid(true);
      setReferralAgentName(option.agentName);
    } else {
      setReferralValid(null);
      setReferralAgentName('');
    }
  };

  // fullAddress から prefecture + city を除いて address フィールドを算出
  const deriveAddress = () => {
    const prefix = prefecture + city;
    if (fullAddress.startsWith(prefix)) {
      return fullAddress.slice(prefix.length);
    }
    return fullAddress;
  };

  // フォームデータを構築
  const buildFormData = (): RegistrationFormData => ({
    email,
    displayName,
    isDonor: false,
    agreedToPrivacyPolicy: agreedToPrivacy,
    agreedToTermsOfService: agreedToTerms,
    agreedToCrossBorderTransfer: agreedToCrossBorder,
    referralCode,
    referralValid,
    wantsDevice: true,
    postalCode,
    prefecture,
    city,
    address: deriveAddress(),
    building: '',
    phoneNumber,
    userRelation,
    userRelationOther,
    wantsWifiPack,
    wantsSetupSupport,
    selectedPlan,
    question,
  });

  // 決済成功後にアカウント作成
  const handleCheckoutSuccess = async () => {
    setShowCheckout(false);
    setLoading(true);
    setError('');

    try {
      const formData = buildFormData();

      // 自動生成パスワードでアカウント作成
      const tempPassword = crypto.randomUUID().slice(0, 16) + 'Aa1!';
      const result = await createUserWithEmailAndPassword(auth, email, tempPassword);

      // パスワード設定メール送信（メール確認も兼ねる）
      await fetch(`${FUNCTIONS_BASE_URL}/email/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      // shared の buildUserDocument で Firestore ドキュメントを構築
      // サブスクはアクティベート時に作成するため、pending_activation で保存
      const userDoc = {
        ...buildUserDocument(
          formData,
          result.user.uid,
          serverTimestamp,
          join.registrationSource as 'landing_page' | 'landing_page_therapy',
        ),
        stripeCustomerId: stripeCustomerId,
        subscriptionStatus: 'pending_activation',
        subscriptionPlanId: selectedPlan,
      };

      await setDoc(doc(db, 'users', result.user.uid), userDoc);

      // Stripe Customer に Firebase UID を紐付け
      try {
        await fetch(`${FUNCTIONS_BASE_URL}/stripe/link-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: stripeCustomerId, userId: result.user.uid }),
        });
      } catch { /* 紐付け失敗は登録自体を止めない */ }

      // 紹介コード記録
      if (referralCode && referralValid) {
        try {
          const idToken = await result.user.getIdToken();
          await fetch(`${FUNCTIONS_BASE_URL}/referral/record`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ referralCode }),
          });
        } catch { /* 登録自体は失敗させない */ }
      }

      trackSignUp(selectedPlan, join.registrationSource, true);
      trackGenerateLead(selectedPlan);
      if (join.registrationSource === 'landing_page') {
        trackTrialSignup();
      }
      setSuccess(true);
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string };
      if (firebaseErr.code === 'auth/email-already-in-use') {
        setError('このメールアドレスは既に登録されています。');
      } else if (firebaseErr.code === 'auth/invalid-email') {
        setError('メールアドレスの形式が正しくありません。');
      } else {
        setError('登録に失敗しました。もう一度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // shared バリデーション
    const formData = buildFormData();
    const errors = validateRegistrationForm(formData);
    if (errors.length > 0) {
      setError(errors[0].message);
      return;
    }

    // fullAddress の追加バリデーション
    if (!fullAddress.trim()) {
      setError('住所を入力してください');
      return;
    }

    setLoading(true);

    try {
      // Stripe Subscription Intent を作成
      const res = await fetch(`${FUNCTIONS_BASE_URL}/stripe/subscription-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, planId: selectedPlan }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || '決済の準備に失敗しました。');
      }

      const { clientSecret, customerId } = await res.json();
      setCheckoutClientSecret(clientSecret);
      setStripeCustomerId(customerId);
      setShowCheckout(true);
    } catch (err: unknown) {
      setError((err as Error).message || '決済の準備に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="lp__join" id={join.sectionId}>
        <p className="lp__join-en">{join.en}</p>
        <h2 className="lp__join-heading">登録完了</h2>
        <div className="lp__join-form-wrap">
          <div className="lp__form-success">
            <p className="lp__form-success__title">{displayName} さん、ありがとうございます！</p>
            <p className="lp__form-success__desc">
              パスワード設定メールを送信しました。<br />
              メールを確認して、パスワードを設定してください。
            </p>
            <a href="https://pichipichipet.web.app/" className="lp__form-success__link">
              たっぷポケットを開く →
            </a>
          </div>
        </div>
        <img className="lp__join-shape" src="/lp-img/round_top_white.svg" alt="" />
      </section>
    );
  }

  return (
    <section className="lp__join" id={join.sectionId}>
      <p className="lp__join-en">{join.en}</p>
      <h2 className="lp__join-heading">{join.heading}</h2>
      <p className="lp__join-sub">{join.subText}</p>

      <div className="lp__join-form-wrap">
        <form ref={formRef} onSubmit={handleSubmit} className="lp__join-form" noValidate>
          {/* プラン選択 */}
          {showPlanSelector && (
            <>
              <div className="lp__form-section">
                <span className="lp__form-section__badge">プラン選択</span>
              </div>
              <div className="lp__form-group">
                <div className="lp__form-plan-group">
                  {plans.map(plan => (
                    <label key={plan.id} className={`lp__form-plan-option${selectedPlan === plan.id ? ' lp__form-plan-option--selected' : ''}`}>
                      <input
                        type="radio"
                        name="selectedPlan"
                        value={plan.id}
                        checked={selectedPlan === plan.id}
                        onChange={e => { setSelectedPlan(e.target.value); trackSelectItem(e.target.value); }}
                      />
                      <span className="lp__form-plan-option__badge">{plan.badge}</span>
                      <span className="lp__form-plan-option__price">月額 {plan.monthlyPrice.value}円（税込）</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* お客様情報 */}
          <div className="lp__form-section">
            <span className="lp__form-section__badge">お客様情報</span>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.displayName}<span className="lp__form-required">※</span></label>
            <input type="text" className="lp__form-input" value={displayName} onChange={e => setDisplayName(sanitizeDisplayName(e.target.value))} placeholder="例：山田 太郎" required />
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.email}<span className="lp__form-required">※</span></label>
            <input type="email" className="lp__form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="例：taro@example.com" required />
          </div>

          <div className="lp__form-group">
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={hasReferral} onChange={e => handleReferralToggle(e.target.checked)} />
              紹介コードをお持ちの方
            </label>
            {hasReferral && (
              <div style={{ marginTop: '0.5rem' }}>
                <select
                  className={`lp__form-select${referralValid === true ? ' lp__form-input--valid' : ''}`}
                  value={referralCode}
                  onChange={e => handleReferralSelect(e.target.value)}
                >
                  <option value="">紹介コードを選択してください</option>
                  {referralOptions.map(opt => (
                    <option key={opt.code} value={opt.code}>{opt.agentName}</option>
                  ))}
                </select>
                {referralValid === true && referralAgentName && (
                  <p className="lp__form-hint lp__form-hint--success">{referralAgentName} からの紹介</p>
                )}
              </div>
            )}
          </div>

          {/* 配送先情報 */}
          <div className="lp__form-section">
            <span className="lp__form-section__badge">配送先情報</span>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.postalCode}<span className="lp__form-required">※</span></label>
            <div className="lp__form-postal-row">
              <span className="lp__form-postal-prefix">〒</span>
              <input type="text" inputMode="numeric" className="lp__form-input" value={postalCode} onChange={e => handlePostalCodeChange(e.target.value)} placeholder="例：1234567（ハイフンなし）" />
              {addressSearching && <span className="lp__form-hint">検索中...</span>}
            </div>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.address}<span className="lp__form-required">※</span></label>
            <input type="text" className="lp__form-input" value={fullAddress} onChange={e => setFullAddress(e.target.value)} placeholder="郵便番号を入力すると自動入力されます" />
            <p className="lp__form-hint">番地・建物名まで入力してください</p>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.phoneNumber}<span className="lp__form-required">※</span></label>
            <input type="tel" inputMode="numeric" className="lp__form-input" value={phoneNumber} onChange={e => handlePhoneChange(e.target.value)} placeholder="例：09012345678（ハイフンなし）" required />
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.userRelation}<span className="lp__form-required">※</span></label>
            <div className="lp__form-radio-group">
              {RELATION_OPTIONS.map(opt => (
                <label key={opt.value} className="lp__form-radio-label">
                  <input type="radio" name="userRelation" value={opt.value} checked={userRelation === opt.value} onChange={e => setUserRelation(e.target.value as DeviceUserRelation)} />
                  {opt.label}
                </label>
              ))}
            </div>
            {userRelation === 'other' && (
              <input type="text" className="lp__form-input" value={userRelationOther} onChange={e => setUserRelationOther(e.target.value.slice(0, 50))} placeholder="関係を入力してください" style={{ marginTop: '0.5rem' }} />
            )}
          </div>

          {/* オプション */}
          <div className="lp__form-section">
            <span className="lp__form-section__badge">オプション</span>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={wantsWifiPack} onChange={e => setWantsWifiPack(e.target.checked)} />
              {FORM_LABELS.wantsWifiPack}
            </label>
            <p className="lp__form-hint">
              {FORM_LABELS.wantsWifiPackDesc}<br />
              {FORM_LABELS.wantsWifiPackPrice}
            </p>
          </div>

          <div className="lp__form-group">
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={wantsSetupSupport} onChange={e => setWantsSetupSupport(e.target.checked)} />
              {FORM_LABELS.wantsSetupSupport}
            </label>
            <p className="lp__form-hint">
              {FORM_LABELS.wantsSetupSupportDesc}<br />
              {FORM_LABELS.wantsSetupSupportPrice}
            </p>
          </div>

          {/* その他 */}
          <div className="lp__form-group">
            <label className="lp__form-label">{FORM_LABELS.question}</label>
            <textarea className="lp__form-textarea" value={question} onChange={e => setQuestion(e.target.value)} />
          </div>

          <div className="lp__form-group">
            <label className="lp__form-label">ご同意事項<span className="lp__form-required">※</span></label>
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={agreedToPrivacy} onChange={e => setAgreedToPrivacy(e.target.checked)} required />
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意する
            </label>
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} required />
              <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer">利用規約</a>に同意する
            </label>
            <label className="lp__form-checkbox">
              <input type="checkbox" checked={agreedToCrossBorder} onChange={e => setAgreedToCrossBorder(e.target.checked)} required />
              {FORM_LABELS.crossBorderTransfer}
            </label>
            <p className="lp__form-hint">
              本サービスは、認証・データ保存・決済・音声認識・会話解析・通話対応等のため、Google（米国）・Stripe（米国）・OpenAI（米国）・Deepgram（米国）・Anthropic（米国）・Photon／Exit Games（ドイツ）等、外国にある第三者に個人データを提供します。移転先国の制度・保護措置の詳細は<a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>をご確認ください。
            </p>
            <p className="lp__form-hint">
              <a href="https://pichipichipet.web.app/commercial-transactions" target="_blank" rel="noopener noreferrer">特定商取引法に基づく表記</a>もご確認ください。
            </p>
          </div>

          {error && <p className="lp__form-error">{error}</p>}

          <CtaButton
            as="button"
            type="submit"
            disabled={loading}
            badge={loading ? '' : join.ctaBadge}
            text={loading ? '登録中...' : join.ctaText}
            trackLocation="join_form"
          />
        </form>
      </div>
      <img className="lp__join-shape" src="/lp-img/round_top_white.svg" alt="" />

      {showCheckout && checkoutClientSecret && (
        <CheckoutModal
          clientSecret={checkoutClientSecret}
          planName={plans.find(p => p.id === selectedPlan)?.badge || '基本プラン'}
          monthlyPrice={plans.find(p => p.id === selectedPlan)?.monthlyPrice.value || '1,980'}
          onSuccess={handleCheckoutSuccess}
          onCancel={() => setShowCheckout(false)}
        />
      )}
    </section>
  );
}
